'use client';

import * as React from 'react';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { CheckIcon, MinusIcon } from '@avodah-inc/ui/icons';
import { cn } from '@avodah-inc/ui/utils';

import { Label } from '../label';

export type CheckboxProps = {
  variant?: 'default' | 'different';
  label?: string;
  dataTestId?: string;
} & React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant = 'default', label, dataTestId, ...props }, ref) => {
  const Icon = variant === 'default' ? CheckIcon : MinusIcon;
  const [isChecked, setIsChecked] = React.useState(props.defaultChecked);

  React.useEffect(() => {
    setIsChecked(props.defaultChecked);
  }, [props.defaultChecked]);

  return (
    <div className="flex h-fit w-fit items-center gap-3">
      <CheckboxPrimitive.Root
        className={cn(
          'focus-visible:ring-ring rounded-xs border-primary-500 ring-offset-primary-600 hover:border-primary-600 data-[state=checked]:border-primary-500 data-[state=checked]:bg-primary-500 data-[state=checked]:hover:border-primary-600 data-[state=checked]:hover:bg-primary-600 peer inline-flex h-4 w-4 shrink-0 items-center justify-center border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-400 data-[state=checked]:text-white data-[state=checked]:disabled:border-gray-400 data-[state=checked]:disabled:bg-gray-400',
          className,
        )}
        defaultChecked={isChecked}
        ref={ref}
        data-testid={dataTestId}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn('flex items-center justify-center text-current')}
        >
          <Icon className="text-white" height={16} width={16} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <Label className="text-body-3 text-onLight-high peer-disabled:text-gray-500">
          {label}
        </Label>
      ) : null}
    </div>
  );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;
